import { Button, Drawer, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { createTemplate, updateTemplate, type ClassSlot, type ClassTemplate } from "../../api/classes";
import { newIdempotencyKey } from "../../api/client";
import { useServices } from "../../app/services";
import k from "./classes.module.css";
import { Field, FormError, messageOf, styles as c, useScope, useStaffNames } from "./common";
import { WEEKDAY_NAMES } from "./time";

interface Draft { name: string; trainerId: string; capacity: string; durationMinutes: string; effectiveFrom: string; effectiveTo: string; slots: ClassSlot[] }

const fresh = (today: string, trainerId: string): Draft => ({ name: "", trainerId, capacity: "20", durationMinutes: "60", effectiveFrom: today, effectiveTo: "", slots: [{ weekday: 1, start: "07:00" }] });
const from = (t: ClassTemplate): Draft => ({ name: t.name, trainerId: t.trainerId, capacity: String(t.capacity), durationMinutes: String(t.durationMinutes), effectiveFrom: t.effectiveFrom, effectiveTo: t.effectiveTo ?? "", slots: t.slots });

/** Returns the first problem in plain words, or null. The API checks everything again. */
export function validateDraft(d: Draft): string | null {
  if (!d.name.trim()) return "Give the class a name.";
  if (!d.trainerId) return "Choose a trainer.";
  const cap = Number(d.capacity), dur = Number(d.durationMinutes);
  if (!Number.isInteger(cap) || cap < 1) return "Capacity must be a whole number of at least 1.";
  if (!Number.isInteger(dur) || dur < 1 || dur > 1439) return "Duration must be 1 to 1439 minutes.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom)) return "Choose the date the class starts.";
  if (d.effectiveTo && d.effectiveTo < d.effectiveFrom) return "The end date is before the start date.";
  if (d.slots.length === 0) return "Add at least one weekly slot.";
  if (d.slots.some((s) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.start))) return "Each slot needs a time as HH:MM.";
  return null;
}

export function TemplateForm({ open, onClose, template }: { open: boolean; onClose: () => void; template?: ClassTemplate }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const staff = useStaffNames();
  const key = useRef(newIdempotencyKey());
  const branchId = template?.branchId ?? sc.ctx.me?.branch?.id ?? sc.branchId ?? "";
  const [draft, setDraft] = useState<Draft>(() => (template ? from(template) : fresh(sc.date, "")));
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const trainerId = draft.trainerId || (staff.list[0]?.id ?? sc.ctx.me?.staff.id ?? "");

  const save = useMutation({
    mutationFn: () => {
      const body = { name: draft.name.trim(), trainerId, capacity: Number(draft.capacity), durationMinutes: Number(draft.durationMinutes), slots: draft.slots, effectiveFrom: draft.effectiveFrom, effectiveTo: draft.effectiveTo || null };
      return template ? updateTemplate(client, template.id, body) : createTemplate(client, { ...body, branchId }, key.current);
    },
    onSuccess: () => { key.current = newIdempotencyKey(); void qc.invalidateQueries({ queryKey: ["classes"] }); toast.show(template ? "Template saved" : "Template created"); onClose(); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    const problem = validateDraft({ ...draft, trainerId });
    if (problem) return setError(problem);
    setError(null);
    save.mutate();
  };
  const slot = (i: number, patch: Partial<ClassSlot>) => set("slots", draft.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title={template ? "Edit class template" : "New class template"} description="A weekly class. The calendar fills itself from this."
      footer={<div className={c.row}><Button variant="primary" busy={save.isPending} onClick={submit}>{template ? "Save template" : "Create template"}</Button><Button variant="plain" onClick={onClose}>Cancel</Button></div>}>
      <form className={`${c.stack} ${c.drawerPad}`} onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
        <Field label="Class name" htmlFor="tpl-name"><input id="tpl-name" className={c.input} value={draft.name} maxLength={100} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Trainer" htmlFor="tpl-trainer">
          <select id="tpl-trainer" className={c.input} value={trainerId} onChange={(e) => set("trainerId", e.target.value)}>
            {staff.list.length === 0 && sc.ctx.me ? <option value={sc.ctx.me.staff.id}>{sc.ctx.me.staff.name}</option> : null}
            {staff.list.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            {trainerId && !staff.list.some((m) => m.id === trainerId) && !(staff.list.length === 0 && trainerId === sc.ctx.me?.staff.id) ? <option value={trainerId}>{staff.nameOf(trainerId)}</option> : null}
          </select>
        </Field>
        <div className={c.row}>
          <Field label="Capacity" htmlFor="tpl-cap"><input id="tpl-cap" className={c.input} inputMode="numeric" value={draft.capacity} onChange={(e) => set("capacity", e.target.value)} /></Field>
          <Field label="Minutes long" htmlFor="tpl-dur"><input id="tpl-dur" className={c.input} inputMode="numeric" value={draft.durationMinutes} onChange={(e) => set("durationMinutes", e.target.value)} /></Field>
        </div>
        <div className={c.row}>
          <Field label="Starts on" htmlFor="tpl-from"><input id="tpl-from" type="date" className={c.input} value={draft.effectiveFrom} onChange={(e) => set("effectiveFrom", e.target.value)} /></Field>
          <Field label="Ends on (optional)" htmlFor="tpl-to"><input id="tpl-to" type="date" className={c.input} value={draft.effectiveTo} onChange={(e) => set("effectiveTo", e.target.value)} /></Field>
        </div>
        <fieldset className={`${c.stack} ${k.plain}`}>
          <legend className={c.note}>Weekly slots</legend>
          {draft.slots.map((s, i) => (
            <div key={i} className={k.slotRow}>
              <Field label={`Day ${i + 1}`} htmlFor={`tpl-day-${i}`}>
                <select id={`tpl-day-${i}`} className={c.input} value={s.weekday} onChange={(e) => slot(i, { weekday: Number(e.target.value) })}>{WEEKDAY_NAMES.map((n, w) => <option key={n} value={w}>{n}</option>)}</select>
              </Field>
              <Field label={`Time ${i + 1}`} htmlFor={`tpl-time-${i}`}><input id={`tpl-time-${i}`} type="time" className={c.input} value={s.start} onChange={(e) => slot(i, { start: e.target.value })} /></Field>
              <Button size="sm" variant="plain" aria-label={`Remove slot ${i + 1}`} onClick={() => set("slots", draft.slots.filter((_, j) => j !== i))}>Remove</Button>
            </div>
          ))}
          <div><Button size="sm" onClick={() => set("slots", [...draft.slots, { weekday: 1, start: "07:00" }])}>Add a slot</Button></div>
        </fieldset>
        <FormError message={error} />
      </form>
    </Drawer>
  );
}
